<script lang="ts" setup>
// Imports
import DynamicDialog from 'primevue/dynamicdialog'

// Composables
const session = useSupabaseSession()
const route = useRoute()

useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] })

// Stores
const commerceStore = useCommerceStore()
const shiftStore = useShiftStore()
const posCheckStore = usePosCheckStore()
const businessStore = useBusinessStore()

// Check if we should show check cart
const { hasActiveCheck, isEditingClosedCheck, checkData } = storeToRefs(posCheckStore)
const { businessInfo, businessId } = storeToRefs(businessStore)

// Check if business uses restaurant (checks/tables) flow
const isChecksFlow = computed(() => businessInfo.value?.service_type === 'restaurant')

// Logger
const log = useLogger('layout:store-cart')

// Loading state
const isInitializing = ref(false)

// Debug: Log service type
watch(() => businessInfo.value?.service_type, (serviceType) => {
  log.info('🏪 POS Mode:', serviceType || 'not set', {
    isRestaurant: serviceType === 'restaurant',
    isCounter: serviceType === 'counter',
    showTables: serviceType === 'restaurant',
    showOrderCart: serviceType === 'counter',
  })
}, { immediate: true })

// Methods
async function initializeStore(buuid: string) {
  businessStore.setBusinessId(buuid)

  await Promise.all([
    commerceStore.initStore(buuid),
    shiftStore.fetchOpenShift(),
  ])
}

// Side effects
watch(() => businessId.value, async (businessIdValue) => {
  if (!businessIdValue && import.meta.client) {
    log.warn('Business ID lost, redirecting to logout')
    await navigateTo('/auth/logout')
    return
  }

  if (businessIdValue && import.meta.client && !import.meta.prerender) {
    await initializeStore(businessIdValue)
  }
}, { immediate: false }) // Don't run immediately during SSR/prerender

// Initialize on client mount (after static page hydration)
onMounted(async () => {
  if (import.meta.client && !import.meta.prerender) {
    isInitializing.value = true

    // CRITICAL: Ensure businessInfo is loaded first
    const success = await businessStore.ensureInitialized()

    if (success && businessId.value) {
      await initializeStore(businessId.value)
    }

    isInitializing.value = false
  }
})
</script>

<template>
  <div class="store-content flex h-screen flex-col bg-gray-100 overflow-hidden print:min-h-0">
    <!-- Loading state during initialization -->
    <div
      v-if="isInitializing"
      class="flex h-screen items-center justify-center"
    >
      <i class="pi pi-spin pi-spinner text-4xl" />
    </div>

    <!-- Main content after initialization -->
    <template v-else>
      <div class="head flex-shrink-0">
        <Toast position="bottom-right" />
        <DynamicDialog />
        <PublicStoreHeader />
        <!-- Edit mode banner -->
        <div
          v-if="isEditingClosedCheck"
          class="bg-amber-400 text-amber-900 text-sm font-semibold px-4 py-2 flex items-center gap-2"
        >
          <i class="pi pi-pencil" />
          {{ $t('Editing closed check') }} #{{ checkData?.check_number?.slice(-4) }} — {{ $t('changes will adjust inventory automatically') }}
        </div>
      </div>
      <div
        v-if="session"
        class="flex-1 sm:gap-2 flex flex-col sm:flex-row overflow-hidden"
      >
        <!-- Main Content Area -->
        <div class="w-full main-content flex-1 flex gap-3 overflow-y-auto print:my-0 print:w-full print:max-w-full print:px-0 print:overflow-visible">
          <div class="w-full">
            <slot />
            <PublicStoreProductsModal />
          </div>
        </div>

        <!-- Right Sidebar: Tables OR Cart (conditional based on mode and active check) - Hidden on mobile -->
        <div
          v-if="route.meta.showCart"
          data-testid="pos-cart-sidebar"
          class="hidden sm:flex my-2 mr-2 sm:mb-0 sm:w-2/6 flex-shrink-0 flex-col h-full"
        >
          <!-- Restaurant mode: Show tables when no check, cart when active -->
          <template v-if="isChecksFlow">
            <Transition
              name="slide-fade"
              mode="out-in"
            >
              <!-- Show table selection when no active check -->
              <POSTablesList
                v-if="!hasActiveCheck"
                key="tables"
              />
              <!-- Show cart when check is active -->
              <POSCheckCart
                v-else
                key="cart"
              />
            </Transition>
          </template>
          <!-- Counter mode: Show CheckCart when active, otherwise show regular cart -->
          <template v-else>
            <POSCheckCart v-if="hasActiveCheck" />
            <PublicStoreCart v-else />
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Slide-fade transition for sidebar switching */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: scale(0.98);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}
</style>
